import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';

import { filter, interval } from 'rxjs';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import packageInfo from '../../../../package.json';
import * as Sentry from '@sentry/angular';

@Injectable({
  providedIn: 'root',
})
export class ServiceWorkerService {
  private deployingNewVersion = false;
  public readonly currentVersion = packageInfo.version;

  constructor(
    private swUpdate: SwUpdate,
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
    private translateService: TranslateService,
    private messageService: MessageService
  ) {
    if (swUpdate.isEnabled) {
      const intervalTime = 1 * 60 * 1000; // 1 min
      interval(intervalTime).subscribe(() =>
        swUpdate.checkForUpdate().catch((error) => {
          console.error('Error checking for updates:', error);
          this.captureServiceWorkerException(error, 'checkForUpdate', 'warning');
        })
      );

      swUpdate.unrecoverable.subscribe((event) => {
        const error = new Error(`Service worker unrecoverable state: ${event.reason}`);
        console.error('Service worker unrecoverable state:', event);

        this.captureServiceWorkerException(error, 'unrecoverable', 'error', {
          reason: event.reason,
          type: event.type,
        });

        this.messageService.add({
          severity: 'error',
          summary: 'App Update Required',
          detail: 'The app needs to be reloaded to continue working properly.',
          sticky: true,
          key: 'unrecoverable-state',
        });

        setTimeout(() => {
          this.refresh();
        }, 3000);
      });

      this.checkForUpdates();
    } else {
      console.log('Service Worker is not enabled in this browser.');
      this.captureServiceWorkerMessage(
        'Service Worker is not enabled in this browser',
        'initialization',
        'info',
        {
          userAgent: navigator.userAgent,
          isSecureContext: window.isSecureContext,
        }
      );
    }
  }

  public checkForUpdates(): void {
    if (!this.swUpdate.isEnabled) {
      console.log('Service Worker updates are disabled.');
      return;
    }

    this.swUpdate.versionUpdates
      .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
      .subscribe((data) => {
        if (!this.deployingNewVersion) {
          this.deployingNewVersion = true;
          console.log('New version available:', data.latestVersion);

          if (this.router.url.includes('/auth')) {
            this.refresh();
          } else {
            this.notifyUpdateAvailable(data.latestVersion);
          }
        }
      });
  }

  async refresh() {
    try {
      await this.swUpdate.activateUpdate();
      this.deployingNewVersion = false;
      console.log('Activating update...');
      this.reloadWindow();
    } catch (error) {
      console.error('Error activating update:', error);
      this.captureServiceWorkerException(error, 'activateUpdate', 'error');
      this.deployingNewVersion = false;
      this.reloadWindow();
    }
  }

  private reloadWindow(): void {
    window.location.reload();
  }

  public manualCheckForUpdate(): Promise<boolean> {
    if (this.swUpdate.isEnabled) {
      return this.swUpdate.checkForUpdate();
    }
    return Promise.resolve(false);
  }

  private notifyUpdateAvailable(latestVersion: VersionReadyEvent['latestVersion']): void {
    this.messageService.add({
      severity: 'info',
      summary: 'Update Available',
      detail: 'A new version of the app is available. The app will reload automatically in 5 seconds.',
      sticky: true,
      key: 'update-available',
    });

    window.dispatchEvent(
      new CustomEvent('sw-update-available', {
        detail: {
          version: latestVersion,
          message: 'A new version of the app is available. The app will reload automatically in 5 seconds.',
        },
      })
    );
  }

  private captureServiceWorkerException(
    error: Error | unknown,
    action: string,
    level: 'error' | 'warning' = 'error',
    extra?: Record<string, unknown>
  ): void {
    Sentry.captureException(error, {
      tags: {
        component: 'ServiceWorker',
        action,
      },
      level,
      extra,
    });
  }

  private captureServiceWorkerMessage(
    message: string,
    action: string,
    level: 'info' | 'warning' | 'error' = 'info',
    extra?: Record<string, unknown>
  ): void {
    Sentry.captureMessage(message, {
      level,
      tags: {
        component: 'ServiceWorker',
        action,
      },
      extra,
    });
  }
}
